import { jsx, jsxs } from 'react/jsx-runtime';
import { createContext, forwardRef } from 'react';
import { Badge } from '../../ui/badge.js';
import { LineIcon } from '../../ui/icons/LineIcon.js';
import { useDevice } from '../../../contexts/DeviceContext.js';
import { cn } from '../../../lib/utils.js';

const RadioContext = createContext(null);
const Radio = forwardRef(
  ({
    variants = "default",
    label,
    disabled = false,
    id,
    value,
    weight = "medium",
    className = "",
    checked,
    ...props
  }, ref) => {
    const handleKeyDown = (e) => {
      if (e.key === "" || e.code === "Space") {
        e.preventDefault();
        const radiobox = e.currentTarget.querySelector(
          'input[type="radio"]'
        );
        if (radiobox && !disabled) {
          radiobox.click();
        }
      }
    };
    const { isDesktop } = useDevice();
    return /* @__PURE__ */ jsx(RadioContext.Provider, { value: { disabled }, children: /* @__PURE__ */ jsxs(
      "label",
      {
        htmlFor: id,
        className: cn(
          "group radio-wrap relative flex items-center gap-2 bg-base-white cursor-pointer",
          variants === "default" && "has-[:focus-visible]:!outline has-[:focus-visible]:outline-2 has-[:focus-visible]:!outline-black has-[:focus-visible]:outline-offset-2",
          variants !== "default" && `rounded-[12px] text-center shadow-[inset_0_0_0_1px_var(--color-border-basic-04)] has-[:checked]:shadow-[inset_0_0_0_2px_#2772c7]
                            has-[:focus-visible]:!border-black has-[:focus-visible]:shadow-[inset_0_0_0_2px_black]`,
          variants === "card" && "card justify-center w-full",
          variants === "button" && "button min-h-[46px] flex-1 justify-center",
          isDesktop && variants === "button" && "min-h-[56px]",
          variants === "button-radio" && "bg-base-white button-radio items-start !min-h-[56px] justify-start pt-4 pb-4 px-4 md:p-5 text-left has-[:disabled]:bg-state-disabled-01 has-[:disabled]:border-state-disabled-02 has-[:checked:disabled]:bg-base-white has-[:checked:disabled]:!border-primary-04",
          variants === "button-small" && "button-small flex-1 justify-center pt-4 pb-[15px] bg-base-white h-[46px] min-w-[88px]",
          variants === "line" && " items-start text-left text-basic-02 !border-t-[1px] !border-basic-05 !px-0 rounded-none has-[:checked]:!border-basic-05 shadow-none has-[:checked]:shadow-none",
          variants === "round" && " items-start bg-base-white button-radio min-h-[56px] justify-start py-3 px-5 text-left ",
          className.includes("selected-disabled") && "!border-primary-04 !bg-base-white",
          className
        ),
        onKeyDown: handleKeyDown,
        children: [
          /* @__PURE__ */ jsxs(
            "span",
            {
              className: cn(
                "relative mt-[2px]",
                variants === "button-radio" && "mt-0",
                (variants === "card" || variants === "button" || variants === "button-small") && "custom-radio",
                className.includes("selected-disabled") && "before:contents-[] before:absolute before:z-[1] before:top-[5px] before:left-[5px] before:w-[14px] before:h-[14px] before:rounded-full before:bg-state-disabled-05"
              ),
              children: [
                /* @__PURE__ */ jsx(
                  "input",
                  {
                    type: "radio",
                    ref,
                    id,
                    value,
                    disabled,
                    ...props,
                    className: cn(
                      "sr-only peer relative w-6 h-6 rounded-full grid border-2 border-basic-01 place-content-center shrink-0 appearance-none ",
                      "checked:border-primary-02",
                      "checked:text-primary-04",
                      "checked:disabled:border-primary-04",
                      "disabled:border-basic-04 disabled:bg-base-white",
                      "focus-visible:outline focus-visible:outline-2 focus-visible:outline-black focus-visible:outline-offset-2",
                      // 'active:disabled:border-primary-04',
                      variants === "button-radio" && "disabled:bg-basic-background-07 disabled:border-basic-04",
                      className.includes("selected-disabled") && "!border-primary-04 !bg-base-white",
                      (variants === "card" || variants === "button" || variants === "button-small") && "custom-radio"
                    ),
                    checked
                  }
                ),
                /* @__PURE__ */ jsx(
                  "span",
                  {
                    className: cn(
                      "absolute top-1 left-1 w-3.5 h-3.5 rounded-full bg-primary-background-04 hidden peer-checked:block peer-checked:peer-disabled:bg-state-disabled-05",
                      (variants === "card" || variants === "button" || variants === "button-small") && "!hidden"
                    )
                  }
                )
              ]
            }
          ),
          label && variants === "card" ? label : /* @__PURE__ */ jsx(
            "span",
            {
              className: cn(
                "pt-0.5 body-lg text-basic-04 group-has-[:checked]:text-primary-04 group-has-[:checked]:[&_dt]:text-primary-04 group-has-[&_dd]:text-basic-04 group-has-[:checked]:[&_dd]:text-primary-04 w-full group-has-[:disabled]:text-state-disabled-04 group-has-[:disabled]:[&_.banner-txt]:text-state-disabled-04",
                // variants === 'button-small' && 'body-md ',
                weight === "bold" && "font-weight-bold",
                weight === "medium" && "font-weight-medium",
                weight === "light" && "font-weight-light",
                variants === "button-radio" && "pt-0"
              ),
              children: label
            }
          )
        ]
      }
    ) });
  }
);
const IconTitleCard = ({
  icon,
  title,
  onClick,
  border
}) => {
  const { isDesktop } = useDevice();
  return /* @__PURE__ */ jsxs(
    "div",
    {
      role: onClick ? "button" : void 0,
      className: cn(
        isDesktop && "h-[200px]",
        "flex flex-col items-center justify-center gap-3 py-[28px] px-5 w-full",
        border && "rounded-[12px] border border-basic-04 active:border-primary-02"
      ),
      onClick,
      children: [
        /* @__PURE__ */ jsx(
          LineIcon,
          {
            icoName: icon,
            icoColor: "var(--color-icon-basic-01)",
            type: isDesktop ? "xl" : "lg",
            className: "flex-grow-0"
          }
        ),
        /* @__PURE__ */ jsx("span", { className: "body-lg text-basic-02 text-center inline-block whitespace-pre-line break-keep font-weight-medium", children: title })
      ]
    }
  );
};
const LabelCard = ({
  icon,
  title,
  desc,
  status,
  statusTitle,
  border = false,
  className,
  linkBtn,
  onClick
}) => {
  const statusContent = {
    ready: {
      color: "information",
      title: "등록가능"
    },
    done: {
      color: "success",
      title: "등록완료"
    },
    blocked: {
      color: "neutral",
      title: "발급불가"
    }
  };
  return /* @__PURE__ */ jsxs(
    "div",
    {
      className: cn(
        "flex gap-2",
        border && "border border-basic-04 rounded-[12px] p-5",
        className?.includes("disabled") && "bg-state-disabled-01 border-state-disabled-02",
        className
      ),
      role: onClick ? "button" : void 0,
      onClick,
      children: [
        icon,
        /* @__PURE__ */ jsxs("div", { className: "flex items-start justify-between gap-2 flex-1", children: [
          /* @__PURE__ */ jsxs("div", { className: "flex flex-col gap-1", children: [
            /* @__PURE__ */ jsx(
              "span",
              {
                className: cn(
                  "label-title body-lg text-basic-04 font-weight-medium group-has-[:checked]:text-primary-04 group-has-[:disabled]:text-state-disabled-04",
                  border && "text-basic-02",
                  className?.includes("disabled") && "text-state-disabled-04"
                ),
                children: title
              }
            ),
            desc && /* @__PURE__ */ jsx(
              "span",
              {
                className: cn(
                  "label-desc body-md font-weight-regular text-basic-04 group-has-[:checked]:text-primary-04 group-has-[:disabled]:text-state-disabled-04",
                  className?.includes("disabled") && "text-state-disabled-04"
                ),
                children: desc
              }
            )
          ] }),
          status && /* @__PURE__ */ jsx(Badge, { variant: "soft", color: statusContent[status].color, children: statusTitle }),
          linkBtn
        ] })
      ]
    }
  );
};

export { IconTitleCard, LabelCard, Radio };
